import React, { useEffect, useRef, useState } from 'react';
import api from '../api/axios';
import { mediaSrc } from '../api/media';

interface Kid {
  id: number;
  name: string;
  username: string;
  grade: number;
}

interface MediaItem {
  id: number;
  type: 'PHOTO' | 'VIDEO';
  filename: string;
  description: string | null;
  visibility: 'EVERYONE' | 'SPECIFIC';
  sharedKidIds: number[];
  sharedWithFriends: boolean;
  url: string;
  uploadedAt: string;
}

interface ParentMediaProps {
  type: 'PHOTO' | 'VIDEO';
}

const ParentMedia: React.FC<ParentMediaProps> = ({ type: mediaType }) => {
  const isPhoto = mediaType === 'PHOTO';
  const [kids, setKids] = useState<Kid[]>([]);
  const [items, setItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadEnabled, setUploadEnabled] = useState(false);

  // upload form state
  const [file, setFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<'EVERYONE' | 'SPECIFIC'>('EVERYONE');
  const [selectedKids, setSelectedKids] = useState<number[]>([]);
  const [sharedWithFriends, setSharedWithFriends] = useState(false);
  const [groups, setGroups] = useState<{ id: number; name: string }[]>([]);
  const [pickedGroups, setPickedGroups] = useState<number[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [kidsRes, mediaRes, profileRes] = await Promise.all([
        api.get('/api/parent/kids'),
        api.get('/api/parent/media', { params: { type: mediaType } }),
        api.get('/api/parent/profile'),
      ]);
      setKids(kidsRes.data);
      setItems(mediaRes.data);
      setUploadEnabled(!!profileRes.data?.mediaUploadEnabled);
      api.get('/api/parent/groups').then((r) => setGroups(r.data)).catch(() => {});
    } catch {}
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAll(); }, [mediaType]);

  const toggleKid = (id: number) =>
    setSelectedKids((prev) => prev.includes(id) ? prev.filter((k) => k !== id) : [...prev, id]);

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!file) { setError('Choose a file first'); return; }
    if (visibility === 'SPECIFIC' && selectedKids.length === 0) {
      setError('Pick at least one kid, or choose "Everyone"');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('type', mediaType);
    fd.append('visibility', visibility);
    if (description.trim()) fd.append('description', description.trim());
    if (visibility === 'SPECIFIC') selectedKids.forEach((id) => fd.append('sharedKidIds', String(id)));
    fd.append('sharedWithFriends', String(sharedWithFriends));
    pickedGroups.forEach((id) => fd.append('sharedGroupIds', String(id)));

    setUploading(true);
    try {
      await api.post('/api/parent/media', fd);
      setFile(null);
      setDescription('');
      setSelectedKids([]);
      setVisibility('EVERYONE');
      setSharedWithFriends(false);
      setPickedGroups([]);
      if (fileInput.current) fileInput.current.value = '';
      fetchAll();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this media?')) return;
    try {
      await api.delete(`/api/parent/media/${id}`);
      fetchAll();
    } catch { alert('Delete failed'); }
  };

  const kidNames = (ids: number[]) =>
    ids.map((id) => kids.find((k) => k.id === id)?.name || `#${id}`).join(', ');

  // ---- backgrounds ----
  const uploadBackground = async (e: React.ChangeEvent<HTMLInputElement>, endpoint: string) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append('file', f);
    try {
      await api.post(endpoint, fd);
      alert('Background updated');
    } catch { alert('Failed to set background'); }
    finally { e.target.value = ''; }
  };

  return (
    <div>
      <main className="space-y-6">
        <h1 className="text-xl font-display font-extrabold text-gray-800">
          {isPhoto ? '📷 Images' : '🎬 Videos'}
        </h1>

        {/* Upload disabled notice */}
        {!loading && !uploadEnabled && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-2xl p-4 text-sm">
            🔒 {isPhoto ? 'Photo' : 'Video'} uploads are currently <strong>disabled</strong> for your account.
            You can still view and delete existing media. Contact the administrator to enable uploads.
          </div>
        )}

        {/* Upload */}
        {uploadEnabled && (
        <section className="bg-white rounded-2xl shadow-sm p-5">
          <h2 className="font-semibold text-gray-700 mb-3">Upload {isPhoto ? 'a Photo' : 'a Video'}</h2>
          <form onSubmit={handleUpload} className="space-y-3">
            <label
              htmlFor="media-file"
              className="flex flex-col items-center justify-center gap-1 w-full border-2 border-dashed border-indigo-300 bg-indigo-50 hover:bg-indigo-100 rounded-xl py-6 px-4 cursor-pointer transition text-center"
            >
              <span className="text-3xl">{isPhoto ? '🖼️' : '🎬'}</span>
              <span className="text-sm font-semibold text-indigo-600">
                {file ? file.name : 'Click to choose a file'}
              </span>
              <span className="text-xs text-gray-400">
                {isPhoto ? 'Images (JPG, PNG, GIF…)' : 'Videos (MP4, MOV…)'}
              </span>
            </label>
            <input
              id="media-file"
              ref={fileInput}
              type="file"
              accept={isPhoto ? 'image/*' : 'video/*'}
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="hidden"
            />

            {/* Description / caption */}
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={200}
              placeholder="Add a description (optional)"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
            />

            {/* Permissions */}
            <div className="border-t pt-3">
              <p className="text-sm font-medium text-gray-600 mb-2">Who can view this?</p>
              <label className="flex items-center gap-2 text-sm mb-2">
                <input type="radio" checked={visibility === 'EVERYONE'} onChange={() => setVisibility('EVERYONE')} />
                Everyone (all my kids)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="radio" checked={visibility === 'SPECIFIC'} onChange={() => setVisibility('SPECIFIC')} />
                Only specific kids
              </label>
              {visibility === 'SPECIFIC' && (
                <div className="mt-2 ml-6 flex flex-wrap gap-3">
                  {kids.map((k) => (
                    <label key={k.id} className="flex items-center gap-1.5 text-sm bg-gray-50 px-2.5 py-1 rounded-lg">
                      <input type="checkbox" checked={selectedKids.includes(k.id)} onChange={() => toggleKid(k.id)} />
                      {k.name}
                    </label>
                  ))}
                  {kids.length === 0 && <span className="text-xs text-gray-400">No kids yet</span>}
                </div>
              )}
            </div>

            {/* Friends visibility */}
            <label className="flex items-center gap-2 text-sm border-t pt-3">
              <input type="checkbox" checked={sharedWithFriends} onChange={(e) => setSharedWithFriends(e.target.checked)} />
              🤝 Also visible to my friends (and connected kids)
            </label>

            {groups.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">👪 Share to groups</p>
                <div className="flex flex-wrap gap-2">
                  {groups.map((g) => (
                    <label key={g.id} className="flex items-center gap-1.5 text-sm bg-gray-50 px-2.5 py-1 rounded-lg">
                      <input type="checkbox" checked={pickedGroups.includes(g.id)}
                        onChange={() => setPickedGroups((p) => p.includes(g.id) ? p.filter((x) => x !== g.id) : [...p, g.id])} />
                      {g.name}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {error && <p className="text-red-500 text-sm">{error}</p>}
            <button type="submit" disabled={uploading}
              className="bg-parentaccent hover:opacity-90 text-white text-sm px-5 py-2 rounded-lg disabled:opacity-50">
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
          </form>
        </section>
        )}

        {/* Backgrounds (photos only) */}
        {uploadEnabled && isPhoto && (
        <section className="bg-white rounded-2xl shadow-sm p-5">
          <h2 className="font-semibold text-gray-700 mb-3">Backgrounds</h2>
          <div className="space-y-2 text-sm">
            <label className="flex items-center justify-between gap-3">
              <span>My dashboard background</span>
              <input type="file" accept="image/*" className="text-xs"
                     onChange={(e) => uploadBackground(e, '/api/parent/background')} />
            </label>
            {kids.map((k) => (
              <label key={k.id} className="flex items-center justify-between gap-3">
                <span>{k.name}'s background</span>
                <input type="file" accept="image/*" className="text-xs"
                       onChange={(e) => uploadBackground(e, `/api/parent/kids/${k.id}/background`)} />
              </label>
            ))}
          </div>
        </section>
        )}

        {/* Gallery */}
        <section>
          <h2 className="font-semibold text-gray-700 mb-3">My {isPhoto ? 'Photos' : 'Videos'}</h2>
          {loading ? (
            <p className="text-gray-400 text-sm">Loading…</p>
          ) : items.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm p-10 text-center text-gray-400">No {isPhoto ? 'photos' : 'videos'} uploaded yet.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {items.map((m) => (
                <div key={m.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
                  {m.type === 'PHOTO' ? (
                    <img src={mediaSrc(m.url)} alt={m.filename} className="w-full aspect-square object-cover" />
                  ) : (
                    <video src={mediaSrc(m.url)} controls className="w-full aspect-square object-cover bg-black" />
                  )}
                  <div className="p-2">
                    <p className="text-[11px] font-semibold text-gray-700 truncate">{m.description || m.filename}</p>
                    <p className="text-[11px] text-gray-400">
                      {m.visibility === 'EVERYONE' ? '👨‍👩‍👧 Everyone' : `🔒 ${kidNames(m.sharedKidIds)}`}
                      {m.sharedWithFriends && <span className="text-indigo-500"> · 🤝 Friends</span>}
                    </p>
                    <button onClick={() => handleDelete(m.id)}
                      className="text-[11px] text-red-400 hover:text-red-600 mt-1">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default ParentMedia;
